// src/ConsumerApp/Screens/Bucket/components/BucketItemCard.js
import React, { useState, useEffect } from 'react';

// Real SVGs from assets/icons
import Bookmark from '../../../../assets/icons/bookmark.svg';
import FillBookmark from '../../../../assets/icons/FillBookmark.svg';
import Trash from '../../../../assets/icons/Trash.svg';
import Plus from '../../../../assets/icons/WhitePlus.svg';
import Minus from '../../../../assets/icons/WhiteMinus.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function BucketItemCard({
  item,
  onUpdateQty,
  onRemove,
  isSavedItem,
  onToggleSave,
}) {
  const id = item.item_id || item._id;
  const name = item.item_name || 'Unknown Product';
  const brand = item.brand || '';
  const unitText = item.uom_name || item.UOM || '';
  const quantity = Number(item.quantity) || 1;
  const minQty = Number(item.min_qty) || 1;
  const maxQty = Number(item.max_qty) > 0 ? Number(item.max_qty) : 999;

  const unitPrice = Number(item.price) || Number(item.unit_price) || 0;
  const itemTotal = Number(item.item_total) || unitPrice * quantity;
  const itemMrpTotal = Number(item.item_mrp_total) || 0;

  const [imgError, setImgError] = useState(false);
  const [inputValue, setInputValue] = useState(String(quantity));
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(Boolean(isSavedItem));

  useEffect(() => {
    setInputValue(String(quantity));
  }, [quantity]);

  useEffect(() => {
    setLocalIsSaved(Boolean(isSavedItem));
  }, [isSavedItem]);

  const handleIncrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) + 1;
    if (next <= maxQty) {
      setInputValue(String(next));
      if (onUpdateQty) onUpdateQty(id, next);
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    const next = (parseInt(inputValue, 10) || 0) - 1;
    if (next < minQty) {
      setShowConfirmDelete(true);
    } else {
      setInputValue(String(next));
      if (onUpdateQty) onUpdateQty(id, next);
    }
  };

  const handleBlur = () => {
    let val = parseInt(inputValue, 10);
    if (isNaN(val) || val < 1) {
      setShowConfirmDelete(true);
      return;
    }
    if (val > maxQty) val = maxQty;
    setInputValue(String(val));
    if (val !== quantity && onUpdateQty) {
      onUpdateQty(id, val);
    }
  };

  const imgPath = item.item_image || item.product_image;
  const imageUrl = imgPath ? `${IMAGE_BASE_URL}${imgPath}` : null;

  return (
    <div style={styles.card}>
      {/* Top Row: Product Thumbnail, Title/Brand/Unit, Actions */}
      <div style={styles.topRow}>
        <div style={styles.imageBox}>
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={name}
              style={styles.image}
              onError={() => setImgError(true)}
            />
          ) : (
            <img src={ToteIcon} alt="fallback" style={{ width: 22, height: 22, opacity: 0.35 }} />
          )}
        </div>

        <div style={styles.infoCol}>
          {brand && <span style={styles.brand}>{brand}</span>}
          <h4 style={styles.name} title={name}>{name}</h4>
          <span style={styles.unit}>
            ₹{unitPrice.toLocaleString('en-IN')} {unitText ? `per ${unitText}` : 'per unit'}
          </span>
        </div>

        <div style={styles.actionGroup}>
          <button
            style={styles.iconBtn}
            onClick={() => {
              const target = !localIsSaved;
              setLocalIsSaved(target);
              if (onToggleSave) onToggleSave(item, target);
            }}
            type="button"
            title="Save for later"
          >
            <img
              src={localIsSaved ? FillBookmark : Bookmark}
              alt="save"
              style={{ width: 14, height: 14 }}
            />
          </button>
          <button
            style={styles.iconBtn}
            onClick={() => setShowConfirmDelete(true)}
            type="button"
            title="Remove"
          >
            <img src={Trash} alt="remove" style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>

      {/* Bottom Row: Price breakdown and Solid Bright Red Zepto Stepper */}
      <div style={styles.bottomRow}>
        <div style={styles.priceContainer}>
          <span style={styles.finalPrice}>₹{itemTotal.toLocaleString('en-IN')}</span>
          {itemMrpTotal > 0 && itemMrpTotal !== itemTotal && (
            <span style={styles.mrpPrice}>₹{itemMrpTotal.toLocaleString('en-IN')}</span>
          )}
        </div>

        {/* Zepto-Style Solid Bright Red Stepper */}
        <div style={styles.zeptoStepper}>
          <button
            style={styles.stepperActionBtn}
            onClick={handleDecrement}
            type="button"
            aria-label="Decrease quantity"
          >
            <img
              src={Minus}
              alt="-"
              style={styles.stepperIcon}
            />
          </button>

          <input
            style={styles.stepperInputText}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={handleBlur}
            maxLength={3}
            aria-label="Quantity"
          />

          <button
            style={styles.stepperActionBtn}
            onClick={handleIncrement}
            type="button"
            aria-label="Increase quantity"
          >
            <img
              src={Plus}
              alt="+"
              style={styles.stepperIcon}
            />
          </button>
        </div>
      </div>

      {/* Delete Confirmation Overlay */}
      {showConfirmDelete && (
        <div style={styles.confirmModalOverlay}>
          <div style={styles.confirmBox}>
            <h5 style={{ margin: '0 0 6px 0', fontSize: 13, color: '#2B2A29' }}>Remove this item?</h5>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowConfirmDelete(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                style={styles.deleteBtn}
                onClick={() => {
                  setShowConfirmDelete(false);
                  if (onRemove) onRemove(id);
                }}
                type="button"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    position: 'relative',
    boxSizing: 'border-box',
    width: '100%',
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  imageBox: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  image: {
    width: '90%',
    height: '90%',
    objectFit: 'contain',
  },
  infoCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  brand: {
    fontSize: 10,
    color: '#707B8C',
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  name: {
    margin: 0,
    fontSize: 13,
    fontWeight: '600',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  unit: {
    fontSize: 11,
    color: '#707B8C',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#F5F3EF',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  finalPrice: {
    fontSize: 14,
    fontWeight: '700',
    color: '#2B2A29',
  },
  mrpPrice: {
    fontSize: 11,
    color: '#707B8C',
    textDecoration: 'line-through',
  },
  /* --- Bright Red Zepto Stepper --- */
  zeptoStepper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#E9001E', // Bright Red token
    borderRadius: 7,
    height: 32,
    width: 88,
    overflow: 'hidden',
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.28)',
  },
  stepperActionBtn: {
    width: 28,
    height: '100%',
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 0.15s ease',
  },
  stepperIcon: {
    width: 16,
    height: 16,
   
  },
  stepperInputText: {
    flex: 1,
    width: '100%',
    border: 'none',
    outline: 'none',
    textAlign: 'center',
    color: '#FFFFFF', // Clean white bold text
    fontWeight: '800',
    fontSize: 13,
    backgroundColor: 'transparent',
    padding: 0,
  },
  confirmModalOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255,255,255,0.96)',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  confirmBox: {
    textAlign: 'center',
  },
  cancelBtn: {
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#2E4057',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteBtn: {
    border: 'none',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: '700',
    cursor: 'pointer',
  },
};